/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */
// Import all CSS first
import 'semantic-ui-css/semantic.min.css'; // Install via: npm install semantic-ui-css
import 'sanitize.css/sanitize.css';

// Then the rest of your imports
import '@babel/polyfill';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import FontFaceObserver from 'fontfaceobserver';
import history from './utils/history';
import GlobalStyle from './global-styles';

// Import root app
import App from 'containers/App';
import configureStore from './configureStore';

// Import i18n messages
import { translationMessages } from './i18n';

// Update icon loading for Vite
let iconPath;
if (location.hostname === 'localhost') {
  iconPath = new URL('./images/survcart_localhost_icon.ico', import.meta.url)
    .href;
} else if (location.hostname === 'app.survcart.dev') {
  iconPath = new URL('./images/survcart_dev_icon.ico', import.meta.url).href;
} else if (location.hostname === 'app.survcart.com') {
  iconPath = new URL('./images/survcart_icon.ico', import.meta.url).href;
} else {
  iconPath = new URL('./images/survcart_dev_icon.ico', import.meta.url).href;
}

// Update favicon
const link = document.createElement('link');
link.rel = 'icon';
link.href = iconPath;
document.head.appendChild(link);

// Load the favicon and the .htaccess file
// import '!file-loader?name=[name].[ext]!./images/' + icon;
// import 'file-loader?name=.htaccess!./.htaccess'; // eslint-disable-line import/extensions

// Observe loading of Open Sans (to remove open sans, remove the <link> tag in
// the index.html file and this observer)
const openSansObserver = new FontFaceObserver('Open Sans', {});

// When Open Sans is loaded, add a font-family using Open Sans to the body
openSansObserver.load().then(() => {
  document.body.classList.add('fontLoaded');
});

// Create redux store with history
const initialState = {};
const store = configureStore(initialState, history);
const MOUNT_NODE = document.getElementById('app');

// Stats setup
window.updateStats = function(key, quantity) {
  if (window.stats[key] === undefined) {
    window.stats[key] = { count: 0, calls: 0 };
  }
  window.stats[key].count += quantity;
  window.stats[key].calls += 1;
  window.stats.totalCount += quantity;
};

// Modern React 18 rendering
const root = createRoot(MOUNT_NODE);

const render = messages => {
  root.render(
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <GlobalStyle />
        <App />
      </ConnectedRouter>
    </Provider>,
  );
};

// Intl polyfill
if (!window.Intl) {
  Promise.all([
    import('intl'),
    import('intl/locale-data/jsonp/en.js'),
    import('intl/locale-data/jsonp/de.js'),
  ])
    .then(() => render(translationMessages))
    .catch(err => {
      throw err;
    });
} else {
  render(translationMessages);
}

// Hot Module Replacement (HMR)
if (import.meta.hot) {
  import.meta.hot.accept(['./i18n', 'containers/App'], () => {
    root.unmount();
    render(translationMessages);
  });
}

// Service Worker for production
if (import.meta.env.PROD) {
  import('offline-plugin/runtime').then(plugin => plugin.install());
}

window.DocketPay = undefined;
let embedImport = document.createElement('script');
if (process.env.VITE_FIREBASE_ENV === 'development') {
  embedImport.setAttribute(
    'src',
    `https://localhost:${process.env.VITE_DOCKET_EMBED_APP_PORT}/embed.js`,
  );
} else if (location.hostname === 'app.survcart.dev') {
  embedImport.setAttribute('src', 'https://embed.survcart.dev/embed.js');
} else if (location.hostname === 'app.survcart.com') {
  embedImport.setAttribute('src', 'https://embed.survcart.com/embed.js');
}
document.head.appendChild(embedImport);

const cssId = 'embed_css';
if (!document.getElementById(cssId)) {
  const head = document.getElementsByTagName('head')[0];
  const elementLink = document.createElement('link');
  elementLink.id = cssId;
  elementLink.rel = 'stylesheet';
  elementLink.type = 'text/css';
  if (process.env.VITE_FIREBASE_ENV === 'development') {
    elementLink.href = `https://localhost:${
      process.env.VITE_DOCKET_EMBED_APP_PORT
    }/embed.css`;
  } else if (location.hostname === 'app.survcart.dev') {
    elementLink.href = 'https://embed.survcart.dev/embed.css';
  } else if (location.hostname === 'app.survcart.com') {
    elementLink.href = 'https://embed.survcart.com/embed.css';
  }

  elementLink.media = 'all';
  head.appendChild(elementLink);
}
